import React, { useState, useEffect, useMemo } from 'react';
import {
    Container, Box, Typography, Paper, Grid, Card, CardContent,
    Stack, Chip, Avatar, IconButton, Button, ToggleButton, ToggleButtonGroup,
    Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem,
    ListItemText, ListItemAvatar, Divider, CircularProgress, Alert
} from '@mui/material';
import {
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Today as TodayIcon,
    CalendarMonth as CalendarIcon,
    Flight as FlightIcon,
    Schedule as ScheduleIcon
} from '@mui/icons-material';
import TravelTimeline from '../components/TravelTimeline';
import { getDabUrl } from '../services/api';

interface TravelFormData {
    id: number;
    staffName: string;
    email: string;
    projectNumber: string;
    projectName: string;
    client: string;
    travelDateFrom: string;
    travelDateTo: string;
    status: 'draft' | 'submitted' | 'approved';
    travelType?: 'project' | 'travel';
    travelReason?: string;
}

const STATUS_COLORS = {
    approved: '#4CAF50',
    submitted: '#2196F3',
    draft: '#FF9800'
};

const STATUS_LABELS = {
    approved: 'Approved',
    submitted: 'Submitted',
    draft: 'Draft'
};

const TravelCalendarPage: React.FC = () => {
    const [travelForms, setTravelForms] = useState<TravelFormData[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [viewMode, setViewMode] = useState<'week' | 'month'>('week');
    const [selectedForm, setSelectedForm] = useState<TravelFormData | null>(null);
    const [showDetailsDialog, setShowDetailsDialog] = useState(false);

    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    useEffect(() => {
        loadTravelForms();
    }, []);

    const loadTravelForms = async () => {
        try {
            setLoading(true);
            const res = await fetch(getDabUrl('TravelOverview'), {
                credentials: 'include'
            });
            if (!res.ok) throw new Error('Failed to fetch travel data');

            const data = await res.json();
            const items = data?.value || data || [];

            const mapped: TravelFormData[] = items.map((item: any) => ({
                id: item.id,
                staffName: item.owner_name || 'Unknown',
                email: item.owner_email || item.owner_user_id || '',
                projectNumber: item.project_number || '',
                projectName: item.project_name || '',
                client: item.client || '',
                travelDateFrom: item.travel_date_from || '',
                travelDateTo: item.travel_date_to || '',
                status: item.status || 'draft',
                travelType: item.travel_type || 'project',
                travelReason: item.travel_reason || ''
            }));

            setTravelForms(mapped);
            setError(null);
        } catch (e) {
            console.error('Failed to load travel forms:', e);
            setError('Failed to load travel data');
        } finally {
            setLoading(false);
        }
    };

    const navigateDate = (direction: 'prev' | 'next') => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            if (viewMode === 'week') {
                newDate.setDate(prev.getDate() + (direction === 'prev' ? -7 : 7));
            } else {
                newDate.setMonth(prev.getMonth() + (direction === 'prev' ? -1 : 1));
            }
            return newDate;
        });
    };

    const goToToday = () => {
        setCurrentDate(new Date());
    };

    const handleFormClick = (form: TravelFormData) => {
        setSelectedForm(form);
        setShowDetailsDialog(true);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const getInitials = (name: string) => {
        if (!name) return '??';
        return name.split(' ').filter(n => n).map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
    };

    const currentlyTravelling = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return travelForms.filter(form => {
            const fromDate = new Date(form.travelDateFrom);
            const toDate = new Date(form.travelDateTo);
            fromDate.setHours(0, 0, 0, 0);
            toDate.setHours(23, 59, 59, 999);
            return today >= fromDate && today <= toDate && form.status === 'approved';
        });
    }, [travelForms]);

    const upcomingThisWeek = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const nextWeek = new Date(today);
        nextWeek.setDate(today.getDate() + 7);
        return travelForms.filter(form => {
            const fromDate = new Date(form.travelDateFrom);
            fromDate.setHours(0, 0, 0, 0);
            return fromDate > today && fromDate <= nextWeek;
        }).sort((a, b) => new Date(a.travelDateFrom).getTime() - new Date(b.travelDateFrom).getTime());
    }, [travelForms]);

    const getViewDateRange = () => {
        if (viewMode === 'week') {
            const startOfWeek = new Date(currentDate);
            const day = startOfWeek.getDay();
            const daysToSubtract = day === 0 ? 6 : day - 1;
            startOfWeek.setDate(startOfWeek.getDate() - daysToSubtract);
            const endOfWeek = new Date(startOfWeek);
            endOfWeek.setDate(startOfWeek.getDate() + 6);
            return `${startOfWeek.getDate()} ${monthNames[startOfWeek.getMonth()].slice(0, 3)} - ${endOfWeek.getDate()} ${monthNames[endOfWeek.getMonth()].slice(0, 3)} ${endOfWeek.getFullYear()}`;
        } else {
            return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
        }
    };

    if (loading) {
        return (
            <Container maxWidth="xl">
                <Box sx={{ py: 8, textAlign: 'center' }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>Loading travel calendar...</Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <Box sx={{ py: 3 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* Header */}
                <Paper sx={{ p: 3, mb: 3, bgcolor: 'transparent', boxShadow: 'none' }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Box sx={{
                                bgcolor: 'primary.main',
                                borderRadius: 2,
                                p: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <CalendarIcon sx={{ color: 'white', fontSize: 32 }} />
                            </Box>
                            <Box>
                                <Typography variant="h4" fontWeight="bold">
                                    Travel Calendar
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    View team travel schedules at a glance
                                </Typography>
                            </Box>
                        </Stack>

                        <Stack direction="row" alignItems="center" spacing={2}>
                            <ToggleButtonGroup
                                value={viewMode}
                                exclusive
                                onChange={(_, value) => value && setViewMode(value)}
                                size="small"
                            >
                                <ToggleButton value="week">Week</ToggleButton>
                                <ToggleButton value="month">Month</ToggleButton>
                            </ToggleButtonGroup>

                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<TodayIcon />}
                                onClick={goToToday}
                            >
                                Today
                            </Button>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2, p: 0.5 }}>
                                <IconButton onClick={() => navigateDate('prev')} size="small">
                                    <ChevronLeftIcon />
                                </IconButton>
                                <Typography variant="subtitle1" sx={{ minWidth: '200px', textAlign: 'center', fontWeight: 600 }}>
                                    {getViewDateRange()}
                                </Typography>
                                <IconButton onClick={() => navigateDate('next')} size="small">
                                    <ChevronRightIcon />
                                </IconButton>
                            </Box>
                        </Stack>
                    </Stack>
                </Paper>

                {/* Legend */}
                <Paper sx={{ p: 2, mb: 3, bgcolor: 'transparent', boxShadow: 'none' }}>
                    <Stack direction="row" alignItems="center" spacing={3} flexWrap="wrap">
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mr: 1 }}>
                            LEGEND:
                        </Typography>
                        {Object.entries(STATUS_COLORS).map(([status, color]) => (
                            <Stack key={status} direction="row" alignItems="center" spacing={1}>
                                <Box sx={{ width: 16, height: 16, bgcolor: color, borderRadius: 0.5 }} />
                                <Typography variant="body2">
                                    {STATUS_LABELS[status as keyof typeof STATUS_LABELS]}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Paper>

                {/* Summary Cards */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%', borderLeft: 4, borderColor: 'success.main' }}>
                            <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                    <FlightIcon color="success" />
                                    <Typography variant="h6" fontWeight="bold">
                                        Currently Travelling
                                    </Typography>
                                    <Chip label={currentlyTravelling.length} size="small" color="success" />
                                </Stack>
                                {currentlyTravelling.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No one is currently travelling
                                    </Typography>
                                ) : (
                                    <List dense disablePadding>
                                        {currentlyTravelling.slice(0, 5).map(form => (
                                            <ListItem
                                                key={form.id}
                                                disablePadding
                                                sx={{ py: 0.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1 }}
                                                onClick={() => handleFormClick(form)}
                                            >
                                                <ListItemAvatar sx={{ minWidth: 40 }}>
                                                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem', bgcolor: STATUS_COLORS.approved }}>
                                                        {getInitials(form.staffName)}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={form.staffName}
                                                    secondary={`${form.projectNumber} • ${form.client}`}
                                                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                                    secondaryTypographyProps={{ variant: 'caption' }}
                                                />
                                            </ListItem>
                                        ))}
                                        {currentlyTravelling.length > 5 && (
                                            <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                                                +{currentlyTravelling.length - 5} more
                                            </Typography>
                                        )}
                                    </List>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%', borderLeft: 4, borderColor: 'info.main' }}>
                            <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                    <ScheduleIcon color="info" />
                                    <Typography variant="h6" fontWeight="bold">
                                        Upcoming This Week
                                    </Typography>
                                    <Chip label={upcomingThisWeek.length} size="small" color="info" />
                                </Stack>
                                {upcomingThisWeek.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No upcoming travel this week
                                    </Typography>
                                ) : (
                                    <List dense disablePadding>
                                        {upcomingThisWeek.slice(0, 5).map(form => {
                                            const daysUntil = Math.ceil((new Date(form.travelDateFrom).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                                            return (
                                                <ListItem
                                                    key={form.id}
                                                    disablePadding
                                                    sx={{ py: 0.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1 }}
                                                    onClick={() => handleFormClick(form)}
                                                >
                                                    <ListItemAvatar sx={{ minWidth: 40 }}>
                                                        <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem', bgcolor: STATUS_COLORS[form.status] }}>
                                                            {getInitials(form.staffName)}
                                                        </Avatar>
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={form.staffName}
                                                        secondary={`${form.projectNumber} • in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}`}
                                                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                                        secondaryTypographyProps={{ variant: 'caption' }}
                                                    />
                                                    <Chip
                                                        label={form.status}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: STATUS_COLORS[form.status],
                                                            color: 'white',
                                                            fontSize: '0.65rem',
                                                            height: 20
                                                        }}
                                                    />
                                                </ListItem>
                                            );
                                        })}
                                        {upcomingThisWeek.length > 5 && (
                                            <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                                                +{upcomingThisWeek.length - 5} more
                                            </Typography>
                                        )}
                                    </List>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Timeline */}
                <Paper sx={{ p: 3, bgcolor: 'transparent', boxShadow: 'none' }}>
                    <TravelTimeline
                        travelForms={travelForms}
                        currentDate={currentDate}
                        viewMode={viewMode}
                        onFormClick={handleFormClick}
                        statusColors={STATUS_COLORS}
                    />
                </Paper>

                {/* Details Dialog */}
                <Dialog
                    open={showDetailsDialog}
                    onClose={() => setShowDetailsDialog(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    {selectedForm && (
                        <>
                            <DialogTitle>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Avatar sx={{ bgcolor: STATUS_COLORS[selectedForm.status] }}>
                                        {getInitials(selectedForm.staffName)}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h6">{selectedForm.staffName}</Typography>
                                        <Chip
                                            label={STATUS_LABELS[selectedForm.status]}
                                            size="small"
                                            sx={{
                                                bgcolor: STATUS_COLORS[selectedForm.status],
                                                color: 'white',
                                                mt: 0.5
                                            }}
                                        />
                                    </Box>
                                </Stack>
                            </DialogTitle>
                            <DialogContent>
                                <Divider sx={{ mb: 2 }} />
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Travel Dates
                                        </Typography>
                                        <Typography variant="body1">
                                            {formatDate(selectedForm.travelDateFrom)} — {formatDate(selectedForm.travelDateTo)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Project
                                        </Typography>
                                        <Typography variant="body1">
                                            {selectedForm.projectNumber}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {selectedForm.projectName}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Client
                                        </Typography>
                                        <Typography variant="body1">
                                            {selectedForm.client || 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Email
                                        </Typography>
                                        <Typography variant="body1">
                                            {selectedForm.email || 'N/A'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setShowDetailsDialog(false)}>Close</Button>
                            </DialogActions>
                        </>
                    )}
                </Dialog>
            </Box>
        </Container>
    );
};

export default TravelCalendarPage;
